import React from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend } from 'chart.js';
import { Q1_2026 } from '../../data/content';
import SectionHeader from '../SectionHeader';
import StatCard from '../StatCard';
import { useIntersection } from '../../hooks/useIntersection';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend);

const G2 = '#2F9E5C';
const GR = '#6B7280';
const GRID = '#E2ECE9';
const tickStyle = { color: GR, font: { size: 10, family: 'Inter' } };
const baseOpts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } };

export default function Q1() {
  const { ref, visible } = useIntersection();

  const monthlyData = {
    labels: Q1_2026.chartData.monthly.labels,
    datasets: [
      {
        type: 'bar',
        data: Q1_2026.chartData.monthly.data,
        backgroundColor: 'rgba(47,158,92,0.6)',
        borderRadius: 4, borderSkipped: false,
      },
      {
        type: 'line',
        data: Q1_2026.chartData.monthly.data,
        borderColor: G2, borderWidth: 2,
        pointRadius: 0, fill: false, tension: 0.4,
      },
    ],
  };

  const q1Data = {
    labels: Q1_2026.chartData.q1Compare.labels,
    datasets: [
      {
        label: 'Q1 2025',
        data: Q1_2026.chartData.q1Compare.q1_2025,
        backgroundColor: 'rgba(168,213,181,0.65)',
        borderRadius: 6, borderSkipped: false,
      },
      {
        label: 'Q1 2026',
        data: Q1_2026.chartData.q1Compare.q1_2026,
        backgroundColor: G2,
        borderRadius: 6, borderSkipped: false,
      },
    ],
  };

  const monthlyOpts = {
    ...baseOpts,
    scales: {
      x: { grid: { display: false }, ticks: { ...tickStyle, autoSkip: false, maxRotation: 0 } },
      y: { grid: { color: GRID }, ticks: { ...tickStyle, callback: v => (v / 1000) + 'K' } },
    },
  };

  const q1Opts = {
    ...baseOpts,
    plugins: { legend: { display: true, labels: { font: { size: 11, family: 'Inter' }, boxWidth: 10, color: GR } } },
    scales: {
      x: { grid: { display: false }, ticks: tickStyle },
      y: { grid: { color: GRID }, ticks: { ...tickStyle, callback: v => (v / 1000) + 'K' } },
    },
  };

  const gridStyle = {
    display: 'grid', gridTemplateColumns: 'repeat(4,1fr)',
    gap: '1px', background: '#D1D5DB',
    border: '1px solid #D1D5DB', borderRadius: 10,
    overflow: 'hidden', marginBottom: 28,
  };

  return (
    <section id="q1" style={{ padding: '72px 0', borderBottom: '1px solid #D1D5DB' }}>
      <div className="r25-section-wrap" style={{ maxWidth: 'var(--max)', margin: '0 auto', padding: '0 40px' }}>
        <SectionHeader num={Q1_2026.sectionNum} title={Q1_2026.title} titleEm={Q1_2026.titleEm} body={Q1_2026.body} />
        <div ref={ref} className={`fade-in${visible ? ' visible' : ''}`}>
          <div className="r25-grid-4" style={gridStyle}>
            {Q1_2026.stats.map((s, i) => <StatCard key={i} {...s} trigger={visible} />)}
          </div>
          <div className="r25-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {[
              { title: 'Monthly Revenue 2025 (AED)', chart: <Bar data={monthlyData} options={monthlyOpts} /> },
              { title: 'Q1 2025 vs Q1 2026 (AED)', chart: <Bar data={q1Data} options={q1Opts} /> },
            ].map(({ title, chart }) => (
              <div key={title} className="r25-chart-wrap" style={{ background: '#fff', borderRadius: 8, padding: 24, border: '1px solid #D1D5DB' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', marginBottom: 16 }}>{title}</div>
                <div style={{ position: 'relative', height: 200 }}>{chart}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
